import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Sidebar.css';
import SvgIcon from './SvgIcon';
import logoImg from '../assets/logo.png';

const Sidebar = ({ activeView, onSwitchView, onLogout, user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [topBarLeftEl, setTopBarLeftEl] = useState(null);

  // Close sidebar drawer when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  // Lock body scroll on small screens when the drawer is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Locate .profile-top-bar-left container to mount mobile toggle horizontally in header frame
  useEffect(() => {
    let observer = null;
    let timer = null;

    const updateTarget = () => {
      const el = document.querySelector('.profile-top-bar-left');
      if (el) {
        setTopBarLeftEl((prev) => (prev === el ? prev : el));
        return true;
      }
      return false;
    };

    if (!updateTarget()) {
      // Observe DOM changes to catch when header mounts after async data fetching
      observer = new MutationObserver(() => {
        if (updateTarget() && observer) {
          observer.disconnect();
          observer = null;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Fallback periodic check
      timer = setInterval(() => {
        if (updateTarget()) {
          clearInterval(timer);
          timer = null;
          if (observer) {
            observer.disconnect();
            observer = null;
          }
        }
      }, 100);
    }

    return () => {
      if (observer) observer.disconnect();
      if (timer) clearInterval(timer);
    };
  }, [activeView]);

  // Touch swipe handling to close drawer on swipe left
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchCurrentX = useRef(0);

  const handleTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      touchCurrentX.current = e.touches[0].clientX;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      touchCurrentX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e) => {
    const deltaX = touchCurrentX.current - touchStartX.current;
    const currentY = e.changedTouches?.[0]?.clientY ?? touchStartY.current;
    const deltaY = Math.abs(currentY - touchStartY.current);

    // Swiped left by at least 40px and horizontal movement is dominant
    if (deltaX < -40 && Math.abs(deltaX) > deltaY) {
      setSidebarOpen(false);
    }
  };

  const userRole = (user?.role || user?.Role || '').trim().toLowerCase();
  const isTrainer = userRole === 'trainer' || (activeView && activeView.startsWith('trainer'));
  const isReportViewer = userRole === 'report viewer' || userRole === 'reportviewer' || userRole === 'viewer';
  const isStaff = !isReportViewer && (userRole === 'admin' || userRole === 'editor' || (!userRole && activeView && activeView.startsWith('admin')));

  const isCoursesActive = activeView === 'adminCourses' || activeView === 'adminCourseBulkImport' || activeView === 'adminTags';
  const isSessionRegistrationActive = activeView === 'adminApprovals' || activeView === 'adminSessionRegistration' || activeView === 'adminRegistrationBulkImport';
  const isAccountRegistrationActive = activeView === 'adminAccountRegistration' || activeView === 'adminAccounts' || activeView === 'adminLearners' || activeView === 'adminAccountUpdates';

  // Keep only the active menu branch expanded, while other menus remain stacked by default
  useEffect(() => {
    setExpandedMenus({
      Courses: Boolean(isCoursesActive),
      'Session Registration': Boolean(isSessionRegistrationActive),
      'Account Registration': Boolean(isAccountRegistrationActive)
    });
  }, [isCoursesActive, isSessionRegistrationActive, isAccountRegistrationActive]);

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const sidebarNavGroups = isTrainer ? [
    {
      category: null,
      items: [
        { label: 'Dashboard', icon: 'grid', view: 'account' },
        { label: 'Training Calendar', icon: 'calendar', view: 'trainerCalendar' },
        { label: 'Profile', icon: 'user', view: 'trainerProfile' },
      ]
    }
  ] : isReportViewer ? [
    {
      category: null,
      items: [
        { label: 'Reports', icon: 'barChart', view: 'adminReports' },
        { label: 'Profile', icon: 'user', view: 'account' },
      ]
    }
  ] : isStaff ? [
    {
      category: 'Overview',
      items: [
        { label: 'Dashboard', icon: 'grid', view: 'adminDashboard' },
        { label: 'Course Calendar', icon: 'calendar', view: 'calendar' },
      ]
    },
    {
      category: 'Operations',
      items: [
        { 
          label: 'Session Registration', 
          icon: 'clipboard', 
          view: 'adminApprovals',
          children: [
            { label: 'Import Registrations', icon: 'download', view: 'adminRegistrationBulkImport' }
          ]
        },
        { 
          label: 'Account Registration', 
          icon: 'users', 
          view: 'adminAccountRegistration',
          children: [
            { label: 'Pending Updates', icon: 'clock', view: 'adminAccountUpdates' },
            { label: 'Directory & Roles', icon: 'users', view: 'adminAccounts' }
          ]
        },
        { 
          label: 'Courses', 
          icon: 'book', 
          view: 'adminCourses',
          children: [
            { label: 'Project Tag Allocation', icon: 'tag', view: 'adminTags' }
          ]
        },
      ]
    },
    {
      category: 'Insights & Feedback',
      items: [
        { label: 'Reports', icon: 'barChart', view: 'adminReports' },
        { label: 'Feedback Management', icon: 'messageSquare', view: 'adminFeedback' },
      ]
    },
    {
      category: 'Administration',
      items: [
        { label: 'Notifications', icon: 'bell', view: 'adminDashboard' },
        { label: 'Audit Logs', icon: 'shield', view: 'adminAuditLogs' },
        { label: 'Application Settings', icon: 'settings', view: 'adminSystemSettings' },
        { label: 'Profile', icon: 'user', view: 'account' },
      ]
    }
  ] : [
    {
      category: null,
      items: [
        { label: 'Dashboard', icon: 'grid', view: 'calendar' },
        { label: 'My Registrations', icon: 'clipboard', view: 'myRegistrations' },
        { label: 'My Certificates', icon: 'award', view: 'myCertificates' },
        { label: 'Profile', icon: 'user', view: 'account' },
      ]
    }
  ];

  const toggleButton = (
    <button 
      type="button"
      className={`sidebar-mobile-toggle ${sidebarOpen ? 'open' : ''}`} 
      onClick={() => setSidebarOpen(!sidebarOpen)}
      aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
      aria-expanded={sidebarOpen}
    >
      <SvgIcon name="menu" size={22} />
    </button>
  );

  return (
    <>
      {/* Mobile Toggle Button (portal into top-bar header frame when available, or fallback) */}
      {topBarLeftEl ? createPortal(toggleButton, topBarLeftEl) : toggleButton}

      {/* Backdrop overlay on mobile */}
      <div 
        className={`app-sidebar-backdrop ${sidebarOpen ? 'open' : ''}`} 
        onClick={() => setSidebarOpen(false)} 
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        aria-hidden="true"
      />

      {/* App Sidebar Navigation Drawer */}
      <aside 
        className={`app-sidebar profile-sidebar ${sidebarOpen ? 'open' : ''}`} 
        aria-label="Main Application Navigation"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="left-panel-top sidebar-brand-container">
          <img src={logoImg} alt="BlackBerry" className="brand-logo sidebar-brand-logo" />
        </div>
        <nav className="sidebar-nav">
          {sidebarNavGroups.map((group, groupIdx) => (
            <div key={group.category || `group-${groupIdx}`} className="sidebar-category-group">
              {group.category && (
                <div 
                  className="sidebar-category-header" 
                  data-testid={`category-header-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span className="sidebar-category-title">{group.category}</span>
                  <span className="sidebar-category-line" aria-hidden="true" />
                </div>
              )}
              {group.items.map(item => {
            const hasChildren = Boolean(item.children && item.children.length > 0);
            const isExpanded = Boolean(expandedMenus[item.label]);
            const isChildActive = hasChildren && item.children.some(child => 
              child.view === activeView || (child.view === 'adminAccounts' && activeView === 'adminLearners')
            );
            const isDirectActive = item.view === activeView || 
              (activeView === 'adminSessionRegistration' && item.view === 'adminApprovals') ||
              (item.view === 'adminCourses' && activeView === 'adminCourseBulkImport');

            return (
              <div key={item.label} className="sidebar-nav-group">
                <button 
                  className={`sidebar-nav-item ${isDirectActive ? 'active' : ''} ${isChildActive ? 'parent-active' : ''} ${hasChildren ? 'has-children' : ''}`} 
                  onClick={() => {
                    if (hasChildren) {
                      if (item.view && item.view !== activeView) {
                        setSidebarOpen(false);
                        setExpandedMenus(prev => ({ ...prev, [item.label]: true }));
                        if (onSwitchView) onSwitchView(item.view);
                      } else {
                        toggleMenu(item.label);
                      }
                    } else {
                      if (item.view === '#') return;
                      setSidebarOpen(false);
                      if (item.view && item.view !== activeView && onSwitchView) onSwitchView(item.view);
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <SvgIcon name={item.icon} size={20} />
                    <span style={{ display: 'inline-flex', flexDirection: 'column', textAlign: 'left', lineHeight: 1.25 }}>
                      <span>{item.label}</span>
                      {item.sublabel && (
                        <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                          {item.sublabel}
                        </span>
                      )}
                    </span>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {item.badge && (
                      <span className="sidebar-badge" style={{
                        backgroundColor: '#4f46e5',
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        marginRight: hasChildren ? '0px' : '8px'
                      }}>
                        {item.badge}
                      </span>
                    )}
                    {hasChildren && (
                      <span 
                        className={`sidebar-chevron ${isExpanded ? 'open' : 'collapsed'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(item.label);
                        }}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                        data-testid={`chevron-${item.label}`}
                      >
                        <SvgIcon name="chevronDown" size={16} />
                      </span>
                    )}
                  </div>
                </button>

                {hasChildren && isExpanded && (
                  <div className="sidebar-sub-menu" role="menu">
                    {item.children.map(child => {
                      const isSubActive = child.view === activeView;
                      return (
                        <button
                          key={child.label}
                          className={`sidebar-sub-item ${isSubActive ? 'active' : ''}`}
                          onClick={() => {
                            if (child.view === '#') return;
                            setSidebarOpen(false);
                            if (child.view && child.view !== activeView && onSwitchView) {
                              onSwitchView(child.view);
                            }
                          }}
                          data-testid={`sub-nav-${child.view}`}
                        >
                          {child.icon && <SvgIcon name={child.icon} size={16} />}
                          <span className="sidebar-sub-label">{child.label}</span>
                          {child.badge && (
                            <span className="sidebar-badge" style={{
                              backgroundColor: '#4f46e5',
                              color: '#ffffff',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '1px 6px',
                              borderRadius: '8px',
                              marginLeft: 'auto'
                            }}>
                              {child.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
          <div className="sidebar-separator" />
          <button 
            type="button"
            className="sidebar-nav-item" 
            onClick={() => {
              setSidebarOpen(false);
            }}
          >
            <SvgIcon name="help" size={20} />Help
          </button>
        </nav>
        <div className="sidebar-nav-bottom">
          <button 
            type="button"
            className="sidebar-nav-item" 
            onClick={() => {
              setSidebarOpen(false);
              if (onLogout) onLogout();
            }}
          >
            <SvgIcon name="logout" size={20} />Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
